import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../components/shared/AuthContext";
import DashboardStats from "./components/DashboardStats";
import MonthlyTrendChart from "./components/MonthlyTrendChart";
import DepositWithdrawChart from "./components/DepositWithdrawChart";
import LoanTable from "./components/LoanTable";
import ArrearTable from "./components/ArrearTable";
import { LogoLoader } from "../../components/ui";

const Dashboard = () => {
  const { roleName, logout, data } = useAuth();

  // Left Trend Chart States
  const [trendMonth, setTrendMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [trendYear, setTrendYear] = useState(() => String(new Date().getFullYear()));
  const [trendData, setTrendData] = useState([]);
  const [isTrendLoading, setIsTrendLoading] = useState(false);

  // Right Donut Chart States
  const [donutMonth, setDonutMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [donutYear, setDonutYear] = useState(() => String(new Date().getFullYear()));
  const [donutData, setDonutData] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    distribution: [
      { name: "Deposits", value: 0, percentage: 0 },
      { name: "Withdrawals", value: 0, percentage: 0 }
    ]
  });
  const [isDonutLoading, setIsDonutLoading] = useState(false);

  // Get the current user's branchId for cache key
  const isSuperAdmin = roleName === 'superadmin';
  const userBranchId = !isSuperAdmin && data?.branchId ? data.branchId : 'all';
  const cacheKey = `dashboardData_${userBranchId}_${roleName || 'guest'}`;

  // Default data structure
  const getDefaultData = () => ({
    customerData: [],
    activeCustomers: [],
    inactiveCustomers: [],
    transactions: [],
    todayLoanList: [],
    yesterdayLoanList: [],
    tomorrowLoanList: [],
    todayArrearList: [],
    todayLoanStats: {
      totalLoansToday: 0,
      totalAmountDue: 0,
      totalOutstanding: 0,
      receivedLoans: 0,
      pendingLoans: 0,
    },
    activeCustomersCount: 0,
    inactiveCustomersCount: 0,
    totalCustomers: 0,
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalLoanDisbursed: 0,
    businessLoan: 0,
    monthlyTrend: [],
    depositsWithdrawalsDistribution: [],
    recentActivities: [],
    emiPayout: 0,
    arrearsStats: {
      totalArrears: 0,
      totalArrearAmount: 0,
      totalOutstanding: 0,
      totalPenalty: 0,
      arrearsWithMobileNumbers: 0,
    },
    outstandingData: {
      totalOutstandingPrincipal: 0,
      totalOutstandingInterest: 0,
      totalOutstandingAmount: 0,
      totalReceivedLoansCount: 0,
    },
    totalPendingPrincipal: {
      totalPendingPrincipal: 0,
      totalPendingLoansCount: 0,
    },
    openingBalance: {
      yesterdayOutstanding: 0,
    },
  });

  // Initialize with empty data - don't use cached data to prevent showing wrong branch data
  const [dashboardData, setDashboardData] = useState(getDefaultData());
  const [isLoading, setIsLoading] = useState(true);
  const [loanFilter, setLoanFilter] = useState("All");
  const [loanDateFilter, setLoanDateFilter] = useState("Today");
  const [arrearFilter, setArrearFilter] = useState("All");
  const [loanSearch, setLoanSearch] = useState("");
  const [loanPage, setLoanPage] = useState(1);
  const [arrearSearch, setArrearSearch] = useState("");
  const [arrearPage, setArrearPage] = useState(1);

  // Track the last fetched branchId to detect changes
  const lastFetchedBranchRef = useRef(null);
  const lastFetchedRoleRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      // Force re-fetch if branchId or roleName changed
      const branchChanged = lastFetchedBranchRef.current !== userBranchId;
      const roleChanged = lastFetchedRoleRef.current !== roleName;

      if (branchChanged || roleChanged) {
        // Clear old cache when switching branches/roles
        if (lastFetchedBranchRef.current !== null) {
          const oldCacheKey = `dashboardData_${lastFetchedBranchRef.current}_${lastFetchedRoleRef.current || 'guest'}`;
          localStorage.removeItem(oldCacheKey);
        }
        // Reset to default data immediately to prevent showing old data
        setDashboardData(getDefaultData());
      }

      setIsLoading(true);

      try {
        // Build URL with branchId and roleName parameters
        let url = `${API_BASE_URL}/api/dashboard/count`;
        const params = new URLSearchParams();

        if (userBranchId !== 'all') {
          params.append('branchId', userBranchId);
        }
        if (roleName) {
          params.append('roleName', roleName);
        }
        // Force refresh to bypass server cache when branch changes
        if (branchChanged || roleChanged) {
          params.append('forceRefresh', 'true');
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        console.log("Fetching dashboard data:", { url, branchId: userBranchId, roleName });

        const response = await axios.get(url, {
          signal: controller.signal,
        });
        console.log("API Response:", response.data, "branchId:", userBranchId);

        const newData = response.data.data || {};
        const defaultLoanStats = {
          totalLoansToday: 0,
          totalAmountDue: 0,
          totalOutstanding: 0,
          receivedLoans: 0,
          pendingLoans: 0,
        };
        const defaultArrearsStats = {
          totalArrears: 0,
          totalArrearAmount: 0,
          totalOutstanding: 0,
          totalPenalty: 0,
          arrearsWithMobileNumbers: 0,
        };
        const defaultOutstandingData = {
          totalOutstandingPrincipal: 0,
          totalOutstandingInterest: 0,
          totalOutstandingAmount: 0,
          totalReceivedLoansCount: 0,
        };
        const defaultPendingPrincipal = {
          totalPendingPrincipal: 0,
          totalPendingLoansCount: 0,
        };
        const defaultOpeningBalance = {
          yesterdayOutstanding: 0,
        };
        const sanitizedData = {
          customerData: newData.customerData || [],
          activeCustomers: newData.activeCustomers || [],
          inactiveCustomers: newData.inactiveCustomers || [],
          transactions: newData.transactions || [],
          todayLoanList: newData.todayLoanList || [],
          yesterdayLoanList: newData.yesterdayLoanList || [],
          tomorrowLoanList: newData.tomorrowLoanList || [],
          todayArrearList: newData.arrearsList || [],
          todayLoanStats: newData.todayLoanStats
            ? { ...defaultLoanStats, ...newData.todayLoanStats }
            : defaultLoanStats,
          activeCustomersCount: newData.activeCustomersCount || 0,
          inactiveCustomersCount: newData.inactiveCustomersCount || 0,
          totalCustomers: newData.totalCustomers || 0,
          totalDeposits: newData.totalDeposits || 0,
          totalWithdrawals: newData.totalWithdrawals || 0,
          totalLoanDisbursed: newData.totalLoanDisbursed || 0,
          businessLoan: newData.businessLoan || 0,
          monthlyTrend: newData.monthlyTrend || [],
          depositsWithdrawalsDistribution: newData.depositsWithdrawalsDistribution || [],
          recentActivities: newData.recentActivities || [],
          emiPayout: newData.emiPayout || 0,
          arrearsStats: newData.arrearsStats
            ? { ...defaultArrearsStats, ...newData.arrearsStats }
            : defaultArrearsStats,
          outstandingData: newData.outstandingData
            ? { ...defaultOutstandingData, ...newData.outstandingData }
            : defaultOutstandingData,
          totalPendingPrincipal: newData.totalPendingPrincipal
            ? { ...defaultPendingPrincipal, ...newData.totalPendingPrincipal }
            : defaultPendingPrincipal,
          openingBalance: newData.openingBalance
            ? { ...defaultOpeningBalance, ...newData.openingBalance }
            : defaultOpeningBalance,
          // Store the branchId this data belongs to
          _branchId: userBranchId,
          _roleName: roleName,
        };

        setDashboardData(sanitizedData);
        // Save to branch-specific cache
        localStorage.setItem(cacheKey, JSON.stringify(sanitizedData));

        // Update refs to track what we fetched
        lastFetchedBranchRef.current = userBranchId;
        lastFetchedRoleRef.current = roleName;
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching dashboard data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => controller.abort();
  }, [roleName, userBranchId, cacheKey]);

  // Fetch left trend chart data separately
  useEffect(() => {
    const controller = new AbortController();
    const fetchTrendData = async () => {
      setIsTrendLoading(true);
      try {
        let url = `${API_BASE_URL}/api/dashboard/count`;
        const params = new URLSearchParams();
        if (userBranchId !== 'all') {
          params.append('branchId', userBranchId);
        }
        if (roleName) {
          params.append('roleName', roleName);
        }
        const timeframe = trendMonth === "all" ? "90_days" : `${trendYear}-${trendMonth}`;
        params.append('timeframe', timeframe);
        
        url += `?${params.toString()}`;
        console.log("Fetching trend chart data:", { url });

        const response = await axios.get(url, { signal: controller.signal });
        const newData = response.data.data || {};
        setTrendData(newData.monthlyTrend || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching trend data:", error);
        }
      } finally {
        setIsTrendLoading(false);
      }
    };

    fetchTrendData();
    return () => controller.abort();
  }, [roleName, userBranchId, trendMonth, trendYear]);

  // Fetch right donut chart data separately
  useEffect(() => {
    const controller = new AbortController();
    const fetchDonutData = async () => {
      setIsDonutLoading(true);
      try {
        let url = `${API_BASE_URL}/api/dashboard/count`;
        const params = new URLSearchParams();
        if (userBranchId !== 'all') {
          params.append('branchId', userBranchId);
        }
        if (roleName) {
          params.append('roleName', roleName);
        }
        const timeframe = donutMonth === "all" ? "90_days" : `${donutYear}-${donutMonth}`;
        params.append('timeframe', timeframe);

        url += `?${params.toString()}`;
        console.log("Fetching donut chart data:", { url });

        const response = await axios.get(url, { signal: controller.signal });
        const newData = response.data.data || {};
        setDonutData({
          totalDeposits: newData.totalDeposits || 0,
          totalWithdrawals: newData.totalWithdrawals || 0,
          distribution: newData.depositsWithdrawalsDistribution || [
            { name: "Deposits", value: 0, percentage: 0 },
            { name: "Withdrawals", value: 0, percentage: 0 }
          ]
        });
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching donut data:", error);
        }
      } finally {
        setIsDonutLoading(false);
      }
    };

    fetchDonutData();
    return () => controller.abort();
  }, [roleName, userBranchId, donutMonth, donutYear]);

  const handleLoanFilterChange = (newFilter) => {
    setLoanFilter(newFilter);
    setLoanPage(1);
  };

  const handleLoanDateFilterChange = (newFilter) => {
    setLoanDateFilter(newFilter);
    setLoanPage(1);
  };

  const handleArrearFilterChange = (newFilter) => {
    setArrearFilter(newFilter);
    setArrearPage(1);
  };

  const getFilteredLoanList = () => {
    let loanList = [];
    switch (loanDateFilter) {
      case "Yesterday":
        loanList = dashboardData.yesterdayLoanList || [];
        break;
      case "Tomorrow":
        loanList = dashboardData.tomorrowLoanList || [];
        break;
      default:
        loanList = dashboardData.todayLoanList || [];
    }

    return loanFilter === "All"
      ? loanList
      : loanList.filter(
        (loan) => loan.received.toLowerCase() === loanFilter.toLowerCase()
      );
  };

  const calculateLoanTotals = () => {
    const loanList = getFilteredLoanList();
    // For collected: use emiReceived if available, otherwise total for received loans
    const collectedTotal = loanList
      .filter((loan) => loan.received.toLowerCase() === "received")
      .reduce((sum, loan) => sum + (loan.emiReceived || loan.total || 0), 0);
    // For pending: use effectiveTotal (which includes arrears) or sum of total + arrearTotal
    const pendingTotal = loanList
      .filter((loan) => loan.received.toLowerCase() === "pending")
      .reduce((sum, loan) => sum + (loan.effectiveTotal || ((loan.total || 0) + (loan.arrearTotal || 0))), 0);

    return { collectedTotal, pendingTotal };
  };

  const { collectedTotal, pendingTotal } = calculateLoanTotals();
  const filteredLoanList = getFilteredLoanList();
  const filteredArrearList =
    arrearFilter === "All"
      ? dashboardData.todayArrearList || []
      : (dashboardData.todayArrearList || []).filter(
        (arrear) => arrear.received.toLowerCase() === arrearFilter.toLowerCase()
      );

  // Search filter
  const searchedLoanList = filteredLoanList.filter((loan) =>
    (loan.customerName || "").toLowerCase().includes(loanSearch.toLowerCase())
  );
  const searchedArrearList = filteredArrearList.filter((arrear) =>
    (arrear.customerName || "").toLowerCase().includes(arrearSearch.toLowerCase())
  );

  // Pagination bounds
  const ITEMS_PER_PAGE = 15;
  const totalLoanPages = Math.ceil(searchedLoanList.length / ITEMS_PER_PAGE) || 1;
  const totalArrearPages = Math.ceil(searchedArrearList.length / ITEMS_PER_PAGE) || 1;

  const paginatedLoanList = searchedLoanList.slice(
    (loanPage - 1) * ITEMS_PER_PAGE,
    loanPage * ITEMS_PER_PAGE
  );
  const paginatedArrearList = searchedArrearList.slice(
    (arrearPage - 1) * ITEMS_PER_PAGE,
    arrearPage * ITEMS_PER_PAGE
  );

  const depositsVal = donutData.totalDeposits || 0;
  const withdrawalsVal = donutData.totalWithdrawals || 0;
  const totalVal = depositsVal + withdrawalsVal;
  const depositsPct = totalVal ? Math.round((depositsVal / totalVal) * 100) : 0;
  const withdrawalsPct = totalVal ? Math.round((withdrawalsVal / totalVal) * 100) : 0;
  const isAllZero = depositsVal === 0 && withdrawalsVal === 0;
  const pieData = isAllZero
    ? [
        { name: "Deposits", value: 50 },
        { name: "Withdrawals", value: 50 },
      ]
    : [
        { name: "Deposits", value: depositsVal },
        { name: "Withdrawals", value: withdrawalsVal },
      ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2.5 rounded-lg shadow-lg border border-border text-sm">
          <p className="text-ink-900 font-semibold mb-2">
            {label}
          </p>
          {payload.map((entry, index) => {
            const isDeposits = entry.name === "Deposits";
            return (
              <div
                key={index}
                className={`flex items-center mb-1 text-xs font-semibold ${
                  isDeposits ? "text-primary-500" : "text-danger-500"
                }`}
              >
                <div
                  className={`w-2.5 h-2.5 rounded-full mr-2 ${
                    isDeposits ? "bg-primary-500" : "bg-danger-500"
                  }`}
                />
                <span className="text-ink-500 font-medium">{entry.name}:</span>
                <span className="ml-1.5 font-bold">
                  ₹{entry.value.toLocaleString("en-IN")}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LogoLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-surface-base min-h-screen">
      <div className="max-w-content mx-auto">
        <DashboardStats dashboardData={dashboardData} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <MonthlyTrendChart
            trendMonth={trendMonth}
            setTrendMonth={setTrendMonth}
            trendYear={trendYear}
            setTrendYear={setTrendYear}
            trendData={trendData}
            isTrendLoading={isTrendLoading}
            CustomTooltip={CustomTooltip}
          />

          <DepositWithdrawChart
            donutMonth={donutMonth}
            setDonutMonth={setDonutMonth}
            donutYear={donutYear}
            setDonutYear={setDonutYear}
            pieData={pieData}
            depositsVal={depositsVal}
            depositsPct={depositsPct}
            withdrawalsVal={withdrawalsVal}
            withdrawalsPct={withdrawalsPct}
            totalVal={totalVal}
            isDonutLoading={isDonutLoading}
            CustomTooltip={CustomTooltip}
            isAllZero={isAllZero}
          />
        </div>

        <div className="flex flex-col gap-6 mb-8">
          <LoanTable
            loanSearch={loanSearch}
            setLoanSearch={setLoanSearch}
            loanDateFilter={loanDateFilter}
            handleLoanDateFilterChange={handleLoanDateFilterChange}
            loanFilter={loanFilter}
            handleLoanFilterChange={handleLoanFilterChange}
            searchedLoanList={searchedLoanList}
            collectedTotal={collectedTotal}
            pendingTotal={pendingTotal}
            paginatedLoanList={paginatedLoanList}
            loanPage={loanPage}
            setLoanPage={setLoanPage}
            totalLoanPages={totalLoanPages}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
          />

          <ArrearTable
            arrearSearch={arrearSearch}
            setArrearSearch={setArrearSearch}
            arrearFilter={arrearFilter}
            handleArrearFilterChange={handleArrearFilterChange}
            searchedArrearList={searchedArrearList}
            paginatedArrearList={paginatedArrearList}
            arrearPage={arrearPage}
            setArrearPage={setArrearPage}
            totalArrearPages={totalArrearPages}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
